import {
  Box,
  Button,
  Divider,
  Drawer,
  Typography,
  alpha,
} from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { NavigationItem } from "app/router/private/navigation";

interface SidebarProps {
  currentPath: string;
  items: NavigationItem[];
  mobileOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

function matchesPathPrefix(currentPath: string, path: string) {
  return currentPath === path || currentPath.startsWith(`${path}/`);
}

function isItemActive(currentPath: string, item: NavigationItem) {
  if (matchesPathPrefix(currentPath, item.path)) {
    return true;
  }

  return (item.activePrefixes ?? []).some((path) =>
    matchesPathPrefix(currentPath, path),
  );
}

function SidebarContent({
  currentPath,
  items,
  onClose,
  onNavigate,
}: Omit<SidebarProps, "mobileOpen">) {
  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 2.2,
        bgcolor: theme.palette.background.paper,
      })}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}>
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = isItemActive(currentPath, item);

          return (
            <Button
              key={item.path}
              onClick={() => {
                onClose();
                onNavigate(item.path);
              }}
              sx={(theme) => ({
                px: 1.3,
                py: 1.15,
                borderRadius: 2.5,
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1.1,
                border: "1px solid",
                borderColor: isActive
                  ? alpha(theme.palette.primary.main, 0.3)
                  : theme.palette.divider,
                bgcolor: isActive
                  ? alpha(theme.palette.primary.main, 0.1)
                  : theme.palette.background.paper,
                textTransform: "none",
                "&:hover": {
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.16)
                    : alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: isActive
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.08),
                  color: isActive
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.secondary,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                })}
              >
                <Icon size={18} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    fontSize: "0.92rem",
                    lineHeight: 1.25,
                  })}
                >
                  {item.label}
                </Typography>
              </Box>

              <ChevronRight
                size={16}
                style={{
                  flexShrink: 0,
                }}
              />
            </Button>
          );
        })}
      </Box>

      <Divider sx={{ my: 2.2 }} />
    </Box>
  );
}

export function Sidebar({
  currentPath,
  items,
  mobileOpen,
  onClose,
  onNavigate,
}: SidebarProps) {
  return (
    <>
      <Box
        sx={(theme) => ({
          display: { xs: "none", lg: "block" },
          borderRight: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        })}
      >
        <Box sx={{ position: "sticky", top: 79, height: "calc(100vh - 79px)" }}>
          <SidebarContent
            currentPath={currentPath}
            items={items}
            onClose={onClose}
            onNavigate={onNavigate}
          />
        </Box>
      </Box>

      <Drawer
        open={mobileOpen}
        onClose={onClose}
        sx={{ display: { xs: "block", lg: "none" } }}
        PaperProps={{
          sx: (theme) => ({
            width: 320,
            maxWidth: "calc(100vw - 24px)",
            bgcolor: theme.palette.background.paper,
          }),
        }}
      >
        <SidebarContent
          currentPath={currentPath}
          items={items}
          onClose={onClose}
          onNavigate={onNavigate}
        />
      </Drawer>
    </>
  );
}
